/**
 * Payment Card Component
 * Individual card component for displaying payment information with progress bar
 * Requirements: 5.6, 6.1, 6.2, 6.3, 6.4, 9.1, 9.2, 9.4, 10.1, 10.2, 10.3, 11.1, 11.3
 */

import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Payment } from '../../../../core/generated/model/payment';
import { Installment } from '../../../../core/generated/model/installment';
import { EnumDescriptionPipe } from '../../../../shared/pipes/enum-description.pipe';
import { fadeIn } from '../../shared/animations';
import { MatDivider } from '@angular/material/divider';

@Component({
  selector: 'app-payment-card',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatProgressBarModule,
    MatTooltipModule,
    EnumDescriptionPipe,
    MatDivider
  ],
  templateUrl: './payment-card.component.html',
  styleUrls: ['./payment-card.component.scss'],
  animations: [fadeIn]
})
export class PaymentCardComponent implements OnInit {
  @Input() payment!: Payment;
  @Output() viewDetails = new EventEmitter<Payment>();
  @Output() payInstallment = new EventEmitter<number>();

  hasOverdue = false;

  ngOnInit(): void {
    this.hasOverdue = this.hasOverdueInstallments();
  }

  /**
   * Get count of paid installments
   * Requirement 5.6: Display installment progress
   */
  getPaidCount(): number {
    return (this.payment.installments || [])
      .filter(i => i.status === Installment.StatusEnum.Paid).length;
  }

  /**
   * Calculate progress percentage
   * Requirement 5.6: Progress percentage accuracy
   */
  getProgressPercentage(): number {
    const total = this.payment.installments?.length || 1;
    const paid = this.getPaidCount();
    return (paid / total) * 100;
  }

  /**
   * Calculate total paid amount
   * Requirement 6.1: Financial summary totals
   */
  getTotalPaid(): number {
    return (this.payment.installments || [])
      .filter(i => i.status === Installment.StatusEnum.Paid)
      .reduce((sum, i) => sum + (i.amount || 0), 0);
  }

  /**
   * Calculate pending amount
   * Requirement 6.2: Financial summary totals
   */
  getPendingAmount(): number {
    return (this.payment.installments || [])
      .filter(i => i.status !== Installment.StatusEnum.Paid && i.status !== Installment.StatusEnum.CancelledPayment)
      .reduce((sum, i) => sum + (i.amount || 0), 0);
  }

  /**
   * Check if payment has overdue installments
   * Requirement 6.3: Overdue detection
   */
  hasOverdueInstallments(): boolean {
    const today = new Date();
    return (this.payment.installments || []).some(i => {
      if (i.status !== Installment.StatusEnum.Paid && i.dueDate) {
        return new Date(i.dueDate) < today;
      }
      return false;
    });
  }

  /**
   * Get count of overdue installments
   * Requirement 6.4: Overdue count
   */
  getOverdueCount(): number {
    const today = new Date();
    return (this.payment.installments || [])
      .filter(i => {
        if (i.status !== Installment.StatusEnum.Paid && i.dueDate) {
          return new Date(i.dueDate) < today;
        }
        return false;
      }).length;
  }

  /**
   * Get status color for mat-chip
   * Requirement 9.2: Status badge color mapping
   */
  getStatusColor(): 'primary' | 'accent' | 'warn' | undefined {
    const colorMap: { [key: string]: 'primary' | 'accent' | 'warn' | undefined } = {
      'PENDING_PAYMENT': 'warn',
      'PAID': 'primary',
      'PARTIALLY_PAID': 'accent',
      'FAILED': 'warn'
    };
    return this.payment.status ? colorMap[this.payment.status] : undefined;
  }

  /**
   * Check if there's a next installment to pay
   * Requirement 10.2: Action buttons
   */
  hasNextInstallmentToPay(): boolean {
    return (this.payment.installments || [])
      .some(i => i.status === Installment.StatusEnum.PendingPayment || i.status === Installment.StatusEnum.Late);
  }

  /**
   * Format date to Brazilian format (DD/MM/YYYY)
   * Requirement 9.1: Display creation date
   */
  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return 'N/A';
    }
  }

  /**
   * Format currency to Brazilian format (R$ X.XXX,XX)
   * Requirement 9.4: Currency formatting
   */
  formatCurrency(value?: number): string {
    if (value === undefined || value === null) return 'R$ 0,00';
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  /**
   * Emit event to view payment details
   * Requirement 10.1: Action buttons
   */
  onViewDetails(): void {
    this.viewDetails.emit(this.payment);
  }

  /**
   * Emit event to pay next installment
   * Requirement 10.2: Pay next installment
   */
  onPayNextInstallment(): void {
    const nextInstallment = (this.payment.installments || [])
      .find(i => i.status === Installment.StatusEnum.PendingPayment || i.status === Installment.StatusEnum.Late);
    
    if (nextInstallment?.id) {
      this.payInstallment.emit(nextInstallment.id);
    }
  }
}
